import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowLeft, RotateCcw, Check, AlertTriangle } from "lucide-react";
import { Reveal, SectionLabel, EASE, useScrollTo, openSchedule } from "./Reveal";import {
  BUSINESS_TYPES, SERVICE_MODELS, MENU_CATEGORIES, emptyMenu, computeKitchenPlan, computeCosts,
} from "../lib/calculatorEngine";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inr = (n) => (n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : `₹${Math.round(n / 1000)}K`);

const LEAD_COUNTRY_CODES = ["+91", "+1", "+44", "+61", "+65", "+971", "+974", "+966"];

const SummaryRow = ({ label, children, testId }) => (
  <div className="grid sm:grid-cols-3 border-b border-ink/10 last:border-0">
    <div className="px-6 lg:px-8 py-4 lg:py-5 font-mono text-[10px] tracking-[0.25em] text-ink/40 uppercase flex items-center">{label}</div>
    <div className="sm:col-span-2 px-6 lg:px-8 py-4 lg:py-5" data-testid={testId}>{children}</div>
  </div>
);

const STEPS = ["YOUR BUSINESS", "YOUR VOLUME", "YOUR SERVICE MODEL", "YOUR MENU", "YOUR OPERATING MODEL", "YOUR RESULTS"];

const OptionCard = ({ selected, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`border px-5 py-4 text-left font-mono text-xs tracking-[0.15em] uppercase transition-colors duration-300 ${
      selected ? "border-skope bg-skope/10 text-skope" : "border-ink/15 text-ink/60 hover:border-ink/40 hover:text-ink"
    }`}
  >
    {children}
  </button>
);

const Seg = ({ options, value, onChange, testId }) => (
  <div className="flex border border-ink/15" data-testid={testId}>
    {options.map((o) => (
      <button
        key={o.value}
        onClick={() => onChange(o.value)}
        data-testid={`${testId}-${o.value}`}
        className={`flex-1 px-2 py-2 font-mono text-[10px] tracking-[0.1em] uppercase transition-colors duration-200 ${
          value === o.value ? "bg-skope text-ink" : "text-ink/50 hover:text-ink"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const NumField = ({ label, value, onChange, testId, min = 0 }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.25em] text-ink/45 uppercase">{label}</span>
    <input
      type="number" min={min} value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="mt-2 w-full bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 font-mono text-2xl text-ink transition-colors duration-300"
    />
  </label>
);

const Results = ({ results, onRestart }) => {
  const scrollTo = useScrollTo();
  const [lead, setLead] = useState({ name: "", email: "", phoneCode: "+91", phone: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");

  const submitLead = async () => {
    setError("");
    if (lead.name.trim().length < 2) return setError("Please enter your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email)) return setError("Please enter a valid email.");
    if (!/^[0-9]{10}$/.test(lead.phone)) return setError("Please enter your 10-digit phone number.");
    setStatus("sending");
    try {
      await axios.post(`${API}/calculator/lead`, {
        name: lead.name, email: lead.email, phone: `${lead.phoneCode} ${lead.phone}`, results,
      });
      setStatus("sent");
    } catch (e) {
      setStatus("error");
      setError(e.response?.data?.detail || "Something went wrong. Please email contact@skopekitchens.com");
    }
  };

  const bandColor = results.complexityBand === "LOW" ? "text-emerald-400" : results.complexityBand === "MEDIUM" ? "text-skope" : "text-amber-400";

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} data-testid="calc-results">
      <div className="border border-ink/10" data-testid="result-summary">
        <div className="px-6 lg:px-8 py-5 border-b border-ink/10 bg-[#f6f6f6] font-mono text-[10px] tracking-[0.3em] text-skope uppercase">
          Your Kitchen At A Glance
        </div>
        <SummaryRow label="Menu Complexity" testId="result-complexity-band">
          <span className={`font-display font-bold text-2xl ${bandColor}`}>{results.complexityBand}</span>
          <span className="ml-3 font-mono text-xs text-ink/40" data-testid="result-complexity-index">{results.complexityIndex}/100</span>
        </SummaryRow>
        <SummaryRow label="Indicative Manpower" testId="result-manpower">
          <span className="font-display font-bold text-2xl text-ink">
            {results.manpowerLow}<span className="text-skope">–{results.manpowerHigh}</span> people
          </span>
        </SummaryRow>
        <SummaryRow label="Suggested Stations" testId="result-stations">
          <span className="text-sm text-ink/70 leading-relaxed">{results.stations.join("  ·  ")}</span>
        </SummaryRow>
        <SummaryRow label="Watch Out For" testId="result-pressure-points">
          <ul className="space-y-1.5">
            {results.pressurePoints.slice(0, 3).map((p) => (
              <li key={p} className="flex gap-2 text-sm text-ink/60 leading-relaxed">
                <AlertTriangle size={13} className="text-amber-400/80 mt-0.5 shrink-0" />{p}
              </li>
            ))}
          </ul>
        </SummaryRow>
      </div>

      {results.costs && (
        <div className="border-x border-b border-ink/10 bg-white" data-testid="result-costs">
          <div className="grid grid-cols-3 border-b border-ink/10 font-mono text-[10px] tracking-[0.15em] uppercase">
            <div className="px-4 lg:px-8 py-4 text-ink/30">Estimated cost</div>
            <div className="px-4 lg:px-8 py-4 text-ink/50">Without Skope</div>
            <div className="px-4 lg:px-8 py-4 text-skope">With Skope</div>
          </div>
          <div className="grid grid-cols-3 border-b border-ink/10 items-center">
            <div className="px-4 lg:px-8 py-5 font-mono text-[10px] tracking-[0.15em] text-ink/40 uppercase">One-time capital</div>
            <div className="px-4 lg:px-8 py-5 font-display font-semibold text-base lg:text-2xl text-ink" data-testid="cost-capex">
              {inr(results.costs.capexLow)}–{inr(results.costs.capexHigh)}
            </div>
            <div className="px-4 lg:px-8 py-5 font-display font-semibold text-base lg:text-2xl text-skope" data-testid="cost-capex-skope">₹0</div>
          </div>
          <div className="grid grid-cols-3 border-b border-ink/10 items-center">
            <div className="px-4 lg:px-8 py-5 font-mono text-[10px] tracking-[0.15em] text-ink/40 uppercase">Monthly cost</div>
            <div className="px-4 lg:px-8 py-5 font-display font-semibold text-base lg:text-2xl text-ink" data-testid="cost-without-monthly">
              {inr(results.costs.withoutMonthlyLow)}–{inr(results.costs.withoutMonthlyHigh)}
            </div>
            <div className="px-4 lg:px-8 py-5 font-display font-semibold text-base lg:text-2xl text-skope" data-testid="cost-with-monthly">
              {inr(results.costs.withMonthlyLow)}–{inr(results.costs.withMonthlyHigh)}
            </div>
          </div>
          <div className="grid grid-cols-3 items-center bg-skope/[0.06]">
            <div className="px-4 lg:px-8 py-5 font-mono text-[10px] tracking-[0.15em] text-skope uppercase">You save</div>
            <div className="px-4 lg:px-8 py-5 font-display text-ink/25">—</div>
            <div className="px-4 lg:px-8 py-5 font-display font-bold text-base lg:text-2xl text-skope" data-testid="cost-savings">
              {inr(results.costs.savingsMonthlyLow)}–{inr(results.costs.savingsMonthlyHigh)}/mo
            </div>
          </div>
          <div className="px-6 lg:px-8 py-6 border-t border-skope/30">
            <p className="text-sm text-ink/60 leading-relaxed">
              Plus <span className="text-ink">{inr(results.costs.capexLow)}–{inr(results.costs.capexHigh)}</span> of capital
              you never spend — roughly <span className="text-skope">{inr(results.costs.savingsYearOneLow)}–{inr(results.costs.savingsYearOneHigh)}</span> saved in year one.
            </p>
            <p className="mt-2 font-mono text-[11px] text-ink/45 tracking-[0.05em]" data-testid="cost-subscription-note">
              Skope subscription starts at ₹75K/month (average estimate) — connect with us for your exact pricing.
            </p>
            <button
              onClick={() => scrollTo("#contact")}
              data-testid="cost-cta"
              className="group mt-5 flex items-center justify-center gap-3 bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-ink hover:text-white transition-colors duration-300 w-full sm:w-auto"
            >
              Start Saving — Talk to Skope
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>
          <p className="px-6 lg:px-8 py-4 font-mono text-[10px] text-ink/30 tracking-[0.08em] leading-relaxed border-t border-ink/10" data-testid="cost-disclaimer">
            Indicative estimates: chef salaries ₹15K–75K/month by complexity, manager + procurement ≈ ₹50K/month,
            typical kitchen build and equipment costs. Final commercials are customised per brand.
          </p>
        </div>
      )}

      <div className="border-x border-b border-ink/10 bg-ink text-white p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6" data-testid="calc-schedule">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-skope uppercase">Ready to discuss your kitchen?</div>
          <p className="mt-2 text-white/60 text-sm max-w-md leading-relaxed">
            Share your requirements with the Skope team and discuss what your kitchen could look like.
          </p>
        </div>
        <button
          onClick={() => openSchedule({
            businessType: results.summary.businessType,
            dailyOrders: results.summary.dailyOrders,
            serviceModel: results.summary.serviceModel,
            complexityBand: results.complexityBand,
            manpower: results.manpower,
            totalItems: results.totalItems,
          })}
          data-testid="calc-schedule-cta"
          className="group flex items-center justify-center gap-3 bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-white hover:text-ink transition-colors duration-300 shrink-0"
        >
          Schedule a Call
          <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
        </button>
      </div>

      <div className="border-x border-b border-ink/10 bg-[#f6f6f6] p-8">
        <p className="font-mono text-[10px] tracking-[0.15em] text-ink/35 uppercase leading-relaxed" data-testid="result-disclaimer">
          These are indicative planning estimates, not a final kitchen design or professional engineering assessment.
          Actual requirements depend on recipes, production volumes, equipment, layout, service model and operating practices.
        </p>
      </div>

      <div className="border-x border-b border-ink/10 bg-white p-8">
        {status === "sent" ? (
          <div className="flex items-start gap-4" data-testid="lead-success">
            <span className="w-10 h-10 border border-skope flex items-center justify-center shrink-0"><Check size={18} className="text-skope" /></span>
            <div>
              <div className="font-display font-semibold text-ink text-lg">Request received.</div>
              <p className="text-sm text-ink/55 mt-1">Your results are on their way to our planning team. Talk to Skope Kitchens — <a href="mailto:contact@skopekitchens.com" className="text-skope underline underline-offset-4">contact@skopekitchens.com</a></p>
            </div>
          </div>
        ) : (
          <>
            <div className="font-display font-semibold text-ink text-xl">GET A PROFESSIONAL ASSESSMENT</div>
            <p className="text-sm text-ink/50 mt-2">Talk to Skope Kitchens — send these results to our planning team.</p>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              <input placeholder="Name *" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} data-testid="lead-name"
                className="bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors" />
              <input placeholder="Email *" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} data-testid="lead-email"
                className="bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors" />
              <div className="flex gap-3">
                <select value={lead.phoneCode} onChange={(e) => setLead({ ...lead, phoneCode: e.target.value })} data-testid="lead-phone-code" aria-label="Country code"
                  className="w-24 shrink-0 bg-white border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors">
                  {LEAD_COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input placeholder="10-digit phone *" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} inputMode="numeric" maxLength={10} data-testid="lead-phone"
                  className="flex-1 min-w-0 bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors placeholder:text-ink/30" />
              </div>
            </div>
            {error && <p className="mt-4 text-sm text-red-400" data-testid="lead-error">{error}</p>}
            <div className="mt-6 flex flex-col sm:flex-row gap-4">
              <button onClick={submitLead} disabled={status === "sending"} data-testid="lead-submit"
                className="flex items-center justify-center gap-3 bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-ink hover:text-white transition-colors disabled:opacity-50">
                {status === "sending" ? "Sending…" : "Get a Professional Assessment"} <ArrowRight size={14} />
              </button>
              <button onClick={onRestart} data-testid="calc-restart"
                className="flex items-center justify-center gap-3 border border-ink/20 text-ink/70 font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:border-skope hover:text-skope transition-colors">
                <RotateCcw size={14} /> Start Again
              </button>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

const Calculator = () => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    businessType: "", dailyOrders: "", peakPerHour: "", operatingHours: "", shifts: "1",
    serviceModel: "", menu: emptyMenu(), sharedIngredients: true,
    peakPattern: "", growthIntent: "",
  });
  const [results, setResults] = useState(null);
  const scrollTo = useScrollTo();

  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const setMenu = (key, patch) => setData((d) => ({ ...d, menu: { ...d.menu, [key]: { ...d.menu[key], ...patch } } }));

  const valid = [
    !!data.businessType,
    Number(data.dailyOrders) > 0 && Number(data.peakPerHour) > 0 && Number(data.operatingHours) > 0 && Number(data.shifts) >= 1,
    !!data.serviceModel,
    MENU_CATEGORIES.some((c) => data.menu[c.key].items > 0),
    !!data.peakPattern && !!data.growthIntent,
    true,
  ][step];

  const next = () => {
    if (step === 4) {
      const plan = computeKitchenPlan({
        ...data,
        dailyOrders: Number(data.dailyOrders), peakPerHour: Number(data.peakPerHour),
        operatingHours: Number(data.operatingHours), shifts: Number(data.shifts),
      });
      plan.costs = computeCosts(plan);
      setResults(plan);
    }
    setStep((s) => s + 1);
    setTimeout(() => scrollTo("#calc-panel"), 60);
  };

  const restart = () => {
    setData({ businessType: "", dailyOrders: "", peakPerHour: "", operatingHours: "", shifts: "1", serviceModel: "", menu: emptyMenu(), sharedIngredients: true, peakPattern: "", growthIntent: "" });
    setResults(null);
    setStep(0);
    setTimeout(() => scrollTo("#calc-panel"), 60);
  };

  return (
    <section id="calculator" className="relative bg-white bg-blueprint-fine py-28 lg:py-40" data-testid="calculator-section">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <Reveal><SectionLabel>Commercial Kitchen Calculator — 03</SectionLabel></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display font-semibold text-3xl lg:text-5xl tracking-tight text-ink leading-[1.08]" data-testid="calculator-headline">
            WHAT DOES YOUR MENU ACTUALLY REQUIRE?
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-5 text-ink/55 max-w-2xl text-base lg:text-lg leading-relaxed">
            Before you invest in space, equipment and manpower, understand the operational complexity of your menu.
          </p>
        </Reveal>

        <div id="calc-panel" className="mt-14 border border-ink/10 bg-white/85 backdrop-blur-xl">
          <div className="flex items-center justify-between border-b border-ink/10 px-6 lg:px-10 py-5">
            <div className="font-mono text-[10px] lg:text-xs tracking-[0.3em] text-ink/50 uppercase" data-testid="calc-step-indicator">
              STEP 0{step + 1} <span className="text-ink/25">/ 06</span> <span className="text-skope ml-3">{STEPS[step]}</span>
            </div>
            <div className="hidden sm:flex gap-1.5">
              {STEPS.map((_, i) => (
                <span key={i} className={`h-1 w-6 transition-colors duration-500 ${i <= step ? "bg-skope" : "bg-ink/10"}`} />
              ))}
            </div>
          </div>

          <div className="p-6 lg:p-10 min-h-[420px]">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
            >
                {step === 0 && (
                  <div data-testid="calc-step-1">
                    <p className="font-mono text-xs text-ink/40 uppercase tracking-[0.2em] mb-6">What are you building?</p>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      {BUSINESS_TYPES.map((b) => (
                        <OptionCard key={b} selected={data.businessType === b} onClick={() => set({ businessType: b })} testId={`biz-${b.toLowerCase().replace(/\s/g, "-")}`}>{b}</OptionCard>
                      ))}
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="grid sm:grid-cols-2 gap-x-12 gap-y-10 max-w-2xl" data-testid="calc-step-2">
                    <NumField label="Expected daily orders / covers" value={data.dailyOrders} onChange={(v) => set({ dailyOrders: v })} testId="input-daily-orders" />
                    <NumField label="Peak orders / covers per hour" value={data.peakPerHour} onChange={(v) => set({ peakPerHour: v })} testId="input-peak-orders" />
                    <NumField label="Operating hours per day" value={data.operatingHours} onChange={(v) => set({ operatingHours: v })} testId="input-operating-hours" />
                    <NumField label="Number of shifts" value={data.shifts} onChange={(v) => set({ shifts: v })} testId="input-shifts" min={1} />
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="calc-step-3">
                    <p className="font-mono text-xs text-ink/40 uppercase tracking-[0.2em] mb-6">How does food reach your customer?</p>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      {SERVICE_MODELS.map((s) => (
                        <OptionCard key={s} selected={data.serviceModel === s} onClick={() => set({ serviceModel: s })} testId={`service-${s.toLowerCase().replace(/\s/g, "-")}`}>{s}</OptionCard>
                      ))}
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div data-testid="calc-step-4">
                    <p className="font-mono text-xs text-ink/40 uppercase tracking-[0.2em] mb-6">Configure each menu category — set item count, complexity and preparation type.</p>
                    <div className="space-y-4">
                      {MENU_CATEGORIES.map((c) => {
                        const m = data.menu[c.key];
                        const activeRow = m.items > 0;
                        return (
                          <div key={c.key} className={`border p-4 lg:p-5 transition-colors duration-300 ${activeRow ? "border-skope/40 bg-skope/[0.03]" : "border-ink/10"}`} data-testid={`menu-row-${c.key}`}>
                            <div className="grid lg:grid-cols-12 gap-4 items-center">
                              <div className="lg:col-span-3 flex items-center gap-4">
                                <input
                                  type="number" min={0} value={m.items}
                                  onChange={(e) => setMenu(c.key, { items: Math.max(0, Number(e.target.value)) })}
                                  data-testid={`menu-items-${c.key}`}
                                  className="w-20 bg-transparent border-b border-ink/20 focus:border-skope outline-none py-1.5 font-mono text-xl text-ink text-center transition-colors"
                                />
                                <span className={`font-mono text-[11px] tracking-[0.15em] uppercase ${activeRow ? "text-ink" : "text-ink/40"}`}>{c.label}</span>
                              </div>
                              <div className="lg:col-span-4">
                                <Seg testId={`menu-complexity-${c.key}`} value={m.complexity} onChange={(v) => setMenu(c.key, { complexity: v })}
                                  options={[{ value: "low", label: "Low" }, { value: "medium", label: "Med" }, { value: "high", label: "High" }]} />
                              </div>
                              <div className="lg:col-span-5">
                                <Seg testId={`menu-prep-${c.key}`} value={m.prep} onChange={(v) => setMenu(c.key, { prep: v })}
                                  options={[{ value: "batch", label: "Batch" }, { value: "cook_to_order", label: "Cook-to-order" }, { value: "mixed", label: "Mixed" }]} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => set({ sharedIngredients: !data.sharedIngredients })}
                      className="mt-6 flex items-center gap-3 group"
                      data-testid="toggle-shared-ingredients"
                    >
                      <span className={`w-10 h-5 border flex items-center px-0.5 transition-colors duration-300 ${data.sharedIngredients ? "border-skope justify-end" : "border-ink/25 justify-start"}`}>
                        <span className={`w-4 h-4 ${data.sharedIngredients ? "bg-skope" : "bg-ink/30"}`} />
                      </span>
                      <span className="font-mono text-[11px] tracking-[0.15em] text-ink/60 uppercase group-hover:text-ink transition-colors">Menu items share common ingredients</span>
                    </button>
                  </div>
                )}

                {step === 4 && (
                  <div className="space-y-12" data-testid="calc-step-5">
                    <div>
                      <p className="font-mono text-xs text-ink/40 uppercase tracking-[0.2em] mb-6">How concentrated is your peak-hour demand?</p>
                      <div className="grid sm:grid-cols-3 gap-3 max-w-3xl">
                        <OptionCard selected={data.peakPattern === "steady"} onClick={() => set({ peakPattern: "steady" })} testId="peak-steady">Steady through the day</OptionCard>
                        <OptionCard selected={data.peakPattern === "moderate"} onClick={() => set({ peakPattern: "moderate" })} testId="peak-moderate">Moderate meal-time spikes</OptionCard>
                        <OptionCard selected={data.peakPattern === "severe"} onClick={() => set({ peakPattern: "severe" })} testId="peak-severe">Severe peak-hour surges</OptionCard>
                      </div>
                    </div>
                    <div>
                      <p className="font-mono text-xs text-ink/40 uppercase tracking-[0.2em] mb-6">Where is this business going?</p>
                      <div className="grid sm:grid-cols-3 gap-3 max-w-3xl">
                        <OptionCard selected={data.growthIntent === "single"} onClick={() => set({ growthIntent: "single" })} testId="growth-single">One great outlet</OptionCard>
                        <OptionCard selected={data.growthIntent === "multi"} onClick={() => set({ growthIntent: "multi" })} testId="growth-multi">Multiple outlets soon</OptionCard>
                        <OptionCard selected={data.growthIntent === "scale"} onClick={() => set({ growthIntent: "scale" })} testId="growth-scale">Build to scale aggressively</OptionCard>
                      </div>
                    </div>
                  </div>
                )}

                {step === 5 && results && <Results results={results} onRestart={restart} />}
            </motion.div>
          </div>

          {step < 5 && (
            <div className="flex items-center justify-between border-t border-ink/10 px-6 lg:px-10 py-5">
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-ink/40 hover:text-ink transition-colors disabled:opacity-0"
                data-testid="calc-back"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <button
                onClick={next}
                disabled={!valid}
                className="flex items-center gap-3 bg-skope text-ink font-mono text-[11px] tracking-[0.2em] uppercase px-7 py-3.5 hover:bg-ink hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="calc-continue"
              >
                {step === 4 ? "See Results" : "Continue"} <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Calculator;
